import { useEffect, useState } from "react";
import { Download, TrendingUp } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";

import { API, api, apiError, fmtMoney } from "@/lib/api";
import { EmptyState, KpiCard, PageHeader, TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SelectField, TextField } from "@/components/forms/Fields";

const COLORS = ["#3b82f6", "#38bdf8", "#22d3ee", "#f59e0b", "#8b5cf6", "#10b981", "#ef4444", "#64748b"];
const tt = {
  contentStyle: { background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, color: "#0f172a" },
  labelStyle: { color: "#475569", fontSize: 11 },
};

const Panel = ({ title, sub, children, action }) => (
  <section className="surface rounded-md p-6">
    <div className="flex flex-wrap items-baseline justify-between gap-3">
      <div>
        <p className="label-xs">{sub}</p>
        <h2 className="mt-1 text-lg font-medium text-slate-900">{title}</h2>
      </div>
      {action}
    </div>
    <div className="mt-6">{children}</div>
  </section>
);

const DataTable = ({ columns, rows, keyer }) => (
  <div className="overflow-x-auto thin-scroll">
    <Table>
      <TableHeader>
        <TableRow className="border-slate-200 hover:bg-transparent">
          {columns.map((c) => (
            <TableHead key={c.key} className="label-xs whitespace-nowrap">{c.label}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={keyer ? keyer(r) : i} className="border-slate-200 odd:bg-slate-50/80">
            {columns.map((c) => (
              <TableCell key={c.key} className={`whitespace-nowrap text-xs ${c.mono ? "font-mono-num text-slate-800" : "text-slate-500"}`}>
                {c.render ? c.render(r) : r[c.key] ?? "—"}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default function Analytics() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    setData(null);
    api.get("/analytics", { params: { period, start: start || undefined, end: end || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(apiError(e));
        setData(false);
      });
  }, [period, start, end]);

  if (data === false)
    return <EmptyState title="Analytics unavailable" description="We could not load the reports right now." />;

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Reporting"
        title="Reports &amp; Analytics"
        subtitle="Funnel conversion, speed metrics, recruiter productivity and source performance."
        actions={
          <Button variant="outline" onClick={() => window.open(`${API}/analytics/export?report=recruiter`, "_blank")}
            data-testid="export-report-button" className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <SelectField label="Period" value={period} onChange={setPeriod} className="w-48" testid="analytics-period-filter"
          options={[
            { value: "today", label: "Today" }, { value: "week", label: "This week" },
            { value: "month", label: "This month" }, { value: "quarter", label: "This quarter" },
            { value: "all", label: "All time" }, { value: "custom", label: "Custom range" },
          ]} />
        {period === "custom" && (
          <>
            <TextField label="From" type="date" value={start} onChange={setStart} testid="analytics-start-date" className="w-44" />
            <TextField label="To" type="date" value={end} onChange={setEnd} testid="analytics-end-date" className="w-44" />
          </>
        )}
      </div>

      {!data ? (
        <TableSkeleton rows={8} cols={5} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Offer to join ratio" value={`${data.offer_to_join.ratio}%`} icon={TrendingUp} tone="emerald" testid="kpi-offer-join" />
            <KpiCard label="Offers accepted" value={data.offer_to_join.accepted} />
            <KpiCard label="Joined" value={data.offer_to_join.joined} tone="cyan" />
            <KpiCard label="Offers declined" value={data.offer_to_join.declined} tone="rose" />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <Panel sub="Conversion" title="Hiring funnel">
              <div className="h-72" data-testid="analytics-funnel-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.funnel}>
                    <CartesianGrid stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="stage" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip {...tt} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {data.funnel.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel sub="Velocity" title="Average days per milestone">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.time_metrics}>
                    <CartesianGrid stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="metric" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={70} />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip {...tt} />
                    <Line type="monotone" dataKey="days" stroke="#38bdf8" strokeWidth={2} dot={{ r: 3, fill: "#38bdf8" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-3 text-[11px] text-slate-400">
                Metrics with no recorded history show as blank rather than an estimate.
              </p>
            </Panel>
          </div>

          <Panel sub="Team" title="Recruiter productivity">
            {data.recruiter_kpis.length === 0 ? (
              <p className="text-xs text-slate-400">No recruiter activity in this period.</p>
            ) : (
              <DataTable
                keyer={(r) => r.recruiter}
                rows={data.recruiter_kpis}
                columns={[
                  { key: "recruiter", label: "Recruiter" },
                  { key: "role", label: "Role" },
                  { key: "sourced", label: "Sourced", mono: true },
                  { key: "screened", label: "Screened", mono: true },
                  { key: "shortlisted", label: "Shortlisted", mono: true },
                  { key: "submissions", label: "Submissions", mono: true },
                  { key: "interviews", label: "Interviews", mono: true },
                  { key: "offers", label: "Offers", mono: true },
                  { key: "joins", label: "Joins", mono: true },
                ]}
              />
            )}
          </Panel>

          <div className="grid gap-6 xl:grid-cols-2">
            <Panel sub="Channels" title="Source performance">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.source_performance}>
                    <CartesianGrid stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="source" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip {...tt} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="candidates" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="submitted" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="joined" fill="#10b981" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel sub="Quality" title="Rejection reasons">
              {data.rejection_analysis.length === 0 ? (
                <p className="text-xs text-slate-400">No rejections recorded.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.rejection_analysis} dataKey="count" nameKey="reason" innerRadius={54} outerRadius={92} paddingAngle={2}>
                        {data.rejection_analysis.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip {...tt} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>
          </div>

          <Panel sub="Accounts" title="Client performance">
            <DataTable
              keyer={(r) => r.client}
              rows={data.client_performance}
              columns={[
                { key: "client", label: "Client" },
                { key: "open_jobs", label: "Open jobs", mono: true },
                { key: "submissions", label: "Submissions", mono: true },
                { key: "interviews", label: "Interviews", mono: true },
                { key: "hires", label: "Hires", mono: true },
                { key: "revenue", label: "Revenue", mono: true, render: (r) => fmtMoney(r.revenue) },
              ]}
            />
          </Panel>

          <Panel sub="Requirements" title="Job performance">
            <DataTable
              keyer={(r) => r.job_code}
              rows={data.job_performance}
              columns={[
                { key: "job", label: "Job" },
                { key: "job_code", label: "Code", mono: true },
                { key: "client", label: "Client" },
                { key: "applicants", label: "Applicants", mono: true },
                { key: "submitted", label: "Submitted", mono: true },
                { key: "joined", label: "Joined", mono: true },
                { key: "status", label: "Status" },
              ]}
            />
          </Panel>

          <Panel sub="Risk" title="Dropout analysis">
            {data.dropout_analysis.length === 0 ? (
              <p className="text-xs text-slate-400">No dropouts recorded.</p>
            ) : (
              <DataTable
                keyer={(r) => r.reason}
                rows={data.dropout_analysis}
                columns={[{ key: "reason", label: "Reason" }, { key: "count", label: "Candidates", mono: true }]}
              />
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
